'use client';

import { useEffect, useRef, useState } from 'react';
import { TechBadge } from '../ui/tech-badge';
import { Icon } from '@iconify/react';
import gsap from 'gsap';
import { type Technology as SanityTechnology } from '@/app/lib/data';
import { useTranslation } from 'react-i18next';

/**
 * Tools section component
 * Displays technologies and tools from Sanity CMS
 * Grouped by category with their respective icons and brand colors
 * Enhanced with professional design and animations
 * Responsive layout for different screen sizes
 */
export function ToolsSection({ technologies }: { technologies: Record<string, SanityTechnology[]> }) {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tl = gsap.timeline();

    if (sectionRef.current) gsap.set(sectionRef.current, { opacity: 0 });

    tl.to(sectionRef.current, { opacity: 1, duration: 0.6 });

    if (headerRef.current) {
      tl.fromTo(headerRef.current, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, ease: "back.out(1.5)" }, "-=0.2");
    }

    if (containerRef.current) {
      tl.fromTo(containerRef.current.children,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, stagger: 0.1, ease: "power2.out" },
        "-=0.3"
      );
    }
  }, []);

  // Add lateral movement animations to tech badges
  useEffect(() => {
    if (!containerRef.current || Object.keys(technologies).length === 0) return;

    const badges = containerRef.current.querySelectorAll('.tech-badge');

    badges.forEach((badge, index) => {
      // Create different animation durations and delays for each badge
      const duration = 10 + Math.random() * 15; // Between 10-25s
      const delay = Math.random() * 5; // Random delay up to 5s
      const direction = index % 2 === 0 ? 'right' : 'left'; // Alternate directions

      const htmlElement = badge as HTMLElement;

      // Add the appropriate animation class
      htmlElement.classList.add(`float-${direction}`);

      // Apply random animation duration and delay
      htmlElement.style.animationDuration = `${duration}s`;
      htmlElement.style.animationDelay = `${delay}s`;
    });
  }, [technologies]);



  // Skeleton loader for tech categories
  const TechCategorySkeleton = () => (
    <div className="space-y-3 animate-pulse">
      <div className="flex items-center gap-2">
        <div className="h-5 w-28 bg-slate-200 dark:bg-slate-700 rounded"></div>
        <div className="h-px flex-grow bg-slate-200 dark:bg-slate-700/50"></div>
      </div>
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-7 w-24 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
        ))}
      </div>
    </div>
  );

  // Order of categories to display
  const categoryOrder = [
    'frontend',
    'backend',
    'database',
    'api',
    'language',
    'css',
    'ai',
    'tool',
    'other'
  ];

  return (
    <section
      ref={sectionRef}
      className="mb-16 sm:mb-20"
    >
      <div
        ref={headerRef}
        className="flex items-center gap-3 mb-6 sm:mb-8"
      >
        <div className="flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 shadow-sm">
          <Icon icon="carbon:tools" className="h-6 w-6 text-primary dark:text-primary/90" />
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{t('Tools.sectionTitle')}</h2>
          <p className="text-sm text-slate-700 dark:text-muted-foreground mt-0.5">{t('Tools.sectionSubtitle')}</p>
        </div>
      </div>

      <div
        className="p-6 sm:p-8 rounded-2xl border border-slate-200/50 dark:border-slate-700/60 bg-white/50 dark:bg-card backdrop-blur-sm shadow-sm"
      >
        <div ref={containerRef} className="space-y-7">
          {(!technologies || Object.keys(technologies).length === 0) ? (
            // Display skeleton loaders if no tech provided
            Array.from({ length: 5 }).map((_, index) => (
              <TechCategorySkeleton key={`skeleton-${index}`} />
            ))
          ) : (
            // Display actual technology categories
            categoryOrder.map(category => (
              technologies[category] && technologies[category].length > 0 && (
                <div key={category} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                      {t(`Tools.categories.${category}` as any)}
                    </h3>
                    <div className="h-px flex-grow bg-slate-200/70 dark:bg-border/30"></div>
                  </div>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {technologies[category].map((tech) => (
                      <TechBadge
                        key={tech._id}
                        name={tech.name}
                        category={t(`Tools.categories.${category}` as any)}
                        icon={tech.iconName}
                        color={tech.brandColor}
                        className="tech-badge"
                      />
                    ))}
                  </div>
                </div>
              )
            ))
          )}
        </div>
      </div>
    </section>
  );
} 